import React, { useEffect, useState } from "react";
import { tr } from '@/i18n/tr';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Sparkles, X } from "lucide-react";
import { App as CapApp } from "@capacitor/app";

/**
 * ApkUpdateChecker — auto-prompts users when a newer APK is available.
 *
 * - Only runs inside the Capacitor Android shell.
 * - Reads current APK version at runtime via @capacitor/app (NOT a JS constant —
 *   critical, because the React bundle is shared across all APK versions via
 *   server.url). So the "current version" shown is always the *real* APK
 *   installed on the phone.
 * - Polls /apk-version.json on mount and every 30 min.
 * - tr('x_2541ca48') opens browser → APK downloads → user installs.
 * - tr('x_153942db') hides for 24h unless force_update=true.
 */

const SKIP_KEY = "sellox_update_skip_until";
const CHECK_INTERVAL_MS = 30 * 60 * 1000;

const isCapacitorAndroid = () => {
  try {
    return !!window.Capacitor?.isNativePlatform?.() && window.Capacitor.getPlatform?.() === "android";
  } catch { return false; }
};

export default function ApkUpdateChecker() {
  const [info, setInfo] = useState(null);
  const [current, setCurrent] = useState({ version: "?", build: 0 });
  const [open, setOpen] = useState(false);

  const check = async () => {
    if (!isCapacitorAndroid()) return;
    try {
      // Real APK version from native layer
      const appInfo = await CapApp.getInfo();
      const curVersion = appInfo.version || "1.0";
      const curBuild = parseInt(appInfo.build || "1", 10);
      setCurrent({ version: curVersion, build: curBuild });

      const res = await fetch("/apk-version.json?t=" + Date.now(), { cache: "no-store" });
      if (!res.ok) return;
      const data = await res.json();
      if (!data?.latest_version_code) return;
      if (data.latest_version_code <= curBuild) return;
      const skipUntil = Number(localStorage.getItem(SKIP_KEY) || 0);
      if (!data.force_update && skipUntil > Date.now()) return;
      setInfo(data);
      setOpen(true);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    check();
    const iv = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, []);

  const handleUpdate = () => {
    const url = info?.download_url || "https://backup.sellox.shop/sellox-latest.apk";
    try { window.open(url, "_system"); } catch { window.location.href = url; }
  };

  const handleSkip = () => {
    localStorage.setItem(SKIP_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
    setOpen(false);
  };

  if (!info) return null;
  const force = !!info.force_update;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!force) setOpen(v); }}>
      <DialogContent className="max-w-sm bg-slate-900 border-emerald-500/30 text-white" translate="no" data-testid="apk-update-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 text-emerald-400">
            <Sparkles className="w-5 h-5" />
            <DialogTitle className="text-base">{tr('x_78f2095f')}</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="flex items-center justify-between px-3 py-2 bg-slate-800/60 rounded-lg">
            <span className="text-slate-400">{tr('x_9b601b8e')}</span>
            <span className="font-mono text-slate-300">v{current.version}</span>
          </div>
          <div className="flex items-center justify-between px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
            <span className="text-emerald-300">{tr('x_9ee20a30')}</span>
            <span className="font-mono text-emerald-400 font-bold">v{info.latest_version}</span>
          </div>

          {info.release_notes && (
            <div className="mt-2 p-3 bg-slate-800/40 rounded-lg border border-slate-700/50">
              <p className="text-[11px] text-slate-400 mb-1.5">{tr('x_49009569')}</p>
              <pre className="whitespace-pre-wrap text-xs text-slate-200 font-sans leading-relaxed">{info.release_notes}</pre>
            </div>
          )}

          {force && (
            <p className="text-xs text-red-400 bg-red-500/10 px-3 py-2 rounded-lg border border-red-500/30">
              {tr('x_b426bb47')}
            </p>
          )}
        </div>

        <div className="flex gap-2 mt-4">
          {!force && (
            <Button variant="outline" onClick={handleSkip} className="flex-1 bg-transparent border-slate-600 text-slate-300 hover:bg-slate-800" data-testid="update-skip-btn">
              <X className="w-4 h-4 mr-1" /> {tr('x_153942db')}
            </Button>
          )}
          <Button onClick={handleUpdate} className="flex-1 bg-emerald-500 hover:bg-emerald-600" data-testid="update-now-btn">
            <Download className="w-4 h-4 mr-1" /> {tr('x_2541ca48')}
          </Button>
        </div>

        <p className="text-[10px] text-slate-500 text-center mt-2">
          {tr('x_fix_apk_download')}
        </p>
      </DialogContent>
    </Dialog>
  );
}
